// src/models/userModels/userProfile.js

export class UserProfile {
  constructor({
    uid,
    name,
    username,
    phone,
    bio = '',
    profileImage = '',
    links = [],
    createdAt = null,
    updatedAt = null,
    subscriberCount = 0,
    followingCount = 0,
    postCount = 0,
  }) {
    this.uid = uid;
    this.name = name;
    this.username = username;
    this.phone = phone;
    this.bio = bio;
    this.profileImage = profileImage;
    this.links = links;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.subscriberCount = subscriberCount;
    this.followingCount = followingCount;
    this.postCount = postCount;
  }

  // ── Factory: from Firestore map ──────────────────────────────────────────

  static fromMap(uid, data) {
    return new UserProfile({
      uid: uid,
      name: data.name || '',
      username: data.username || '',
      phone: data.phone || '',
      bio: data.bio || '',
      profileImage: data.profileImage || '',
      links: data.links || [],
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt || null,
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt || null,
      subscriberCount: data.subscriberCount || 0,
      followingCount: data.followingCount || 0,
      postCount: data.postCount || 0,
    });
  }

  // ── toMap ──────────────────────────────────────────────────────────────────

  toMap() {
    return {
      uid: this.uid,
      name: this.name,
      username: this.username,
      phone: this.phone,
      bio: this.bio,
      profileImage: this.profileImage,
      links: this.links,
      subscriberCount: this.subscriberCount,
      followingCount: this.followingCount,
      postCount: this.postCount,
    };
  }

  // ── Display name getter ──────────────────────────────────────────────────

  get displayName() {
    if (this.name && this.name.length > 0) return this.name;
    if (this.username && this.username.length > 0) return this.username;
    return this.phone;
  }

  // ── hasProfileImage ──────────────────────────────────────────────────────

  get hasProfileImage() {
    return this.profileImage &&
      this.profileImage.length > 0 &&
      this.profileImage.startsWith('http');
  }

  // ── copyWith ──────────────────────────────────────────────────────────────

  copyWith({
    name,
    username,
    phone,
    bio,
    profileImage,
    links,
    subscriberCount,
    followingCount,
    postCount,
  }) {
    return new UserProfile({
      uid: this.uid,
      name: name ?? this.name,
      username: username ?? this.username,
      phone: phone ?? this.phone,
      bio: bio ?? this.bio,
      profileImage: profileImage ?? this.profileImage,
      links: links ?? this.links,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      subscriberCount: subscriberCount ?? this.subscriberCount,
      followingCount: followingCount ?? this.followingCount,
      postCount: postCount ?? this.postCount,
    });
  }

  // ── toJson ─────────────────────────────────────────────────────────────────

  toJson() {
    return {
      uid: this.uid,
      name: this.name,
      username: this.username,
      phone: this.phone,
      bio: this.bio,
      profileImage: this.profileImage,
      links: this.links,
      createdAt: this.createdAt ? this.createdAt.toISOString() : null,
      updatedAt: this.updatedAt ? this.updatedAt.toISOString() : null,
      subscriberCount: this.subscriberCount,
      followingCount: this.followingCount,
      postCount: this.postCount,
    };
  }

  // ── fromJson ──────────────────────────────────────────────────────────────

  static fromJson(uid, json) {
    return new UserProfile({
      uid: uid,
      name: json.name || '',
      username: json.username || '',
      phone: json.phone || '',
      bio: json.bio || '',
      profileImage: json.profileImage || '',
      links: json.links || [],
      createdAt: json.createdAt ? new Date(json.createdAt) : null,
      updatedAt: json.updatedAt ? new Date(json.updatedAt) : null,
      subscriberCount: json.subscriberCount || 0,
      followingCount: json.followingCount || 0,
      postCount: json.postCount || 0,
    });
  }

  // ── Static empty ──────────────────────────────────────────────────────────

  static get empty() {
    return new UserProfile({
      uid: '',
      name: '',
      username: '',
      phone: '',
      bio: '',
      profileImage: '',
      links: [],
      createdAt: null,
      updatedAt: null,
      subscriberCount: 0,
      followingCount: 0,
      postCount: 0,
    });
  }

  toString() {
    return `UserProfile(uid: ${this.uid}, name: ${this.name}, username: ${this.username})`;
  }
}

export default UserProfile;